import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Data, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { combineLatest, filter, firstValueFrom, map, Subscription } from 'rxjs';
import { CheckForUpdateService } from '../wonder-core-ng/service-worker/check-for-update.service';
import { HandleUnrecoverableStateService } from '../wonder-core-ng/service-worker/handle-unrecoverable-state.service';
import { PromptUpdateService } from '../wonder-core-ng/service-worker/prompt-update.service';
import { ResponsiveService } from '../wonder-core-ng/services/responsive.service';
import { SessionStoreModule } from '../wonder-core-ng/session-store/session-store.module';
import { SessionService } from '../wonder-core-ng/session-store/session.service';
import { FrameDetectionService } from '@wonder-bi-front/frame-detection.service';
import { HeaderBarComponent } from '@wonder-bi-front/header-bar/header-bar.component';
import { InfoMessageComponent } from './info-message/info-message.component';
import { expandCollapseJiggleAnimation } from './interfaces/animations';
import { ISectionData } from './interfaces/section.interface';
import { LayoutService } from './menu/layout.service';
import { MenuComponent } from './menu/menu.component';

@Component({
  selector: 'wbi-root',
  imports: [
    RouterOutlet,
    MatSidenavModule,
    InfoMessageComponent,
    MatIconModule,
    MenuComponent,
    SessionStoreModule,
    HeaderBarComponent,
    CommonModule,
  ],
  providers: [
    ResponsiveService,
    PromptUpdateService,
    HandleUnrecoverableStateService,
    CheckForUpdateService,
  ],
  animations: [expandCollapseJiggleAnimation],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly promptUpdate = inject(PromptUpdateService);
  private readonly handleUnrecoverableState = inject(HandleUnrecoverableStateService);
  private readonly checkForUpdate = inject(CheckForUpdateService);
  public readonly iframeDetection = inject(FrameDetectionService);
  private readonly matIconRegistry = inject(MatIconRegistry);

  public responsiveService = inject(ResponsiveService);
  public layoutService = inject(LayoutService);
  private readonly sessionService = inject(SessionService);
  showMenu = computed(() => !this.iframeDetection.inIframe() && this.sessionService.isLoggedIn());
  isScrolled = signal(false);
  title = 'WonderBI';
  sectionData: WritableSignal<ISectionData> = signal({
    svgIcon: null,
    icon: null,
    name: '',
  });
  hideNavbar = signal(false);
  showHeaderBar = computed(() => !this.responsiveService.handset());
  currentColor: string = 'magenta';
  COLORS = {
    magenta: '#e64487',
    purple: '#c966cd',
    turquoise: '#55becf',
  };
  closeMenuSignal = signal(false);
  darkMode = signal(false);
  setDarkMode = effect(() => {
    document.documentElement.classList.toggle('dark', this.darkMode());
  });

  sidenavMode$ = combineLatest([
    this.responsiveService.responsive$,
    this.layoutService.drawerShrink$,
  ]).pipe(map(([responsive, shrink]) => (!responsive || shrink ? 'side' : 'over')));
  sidenavMode = toSignal(this.sidenavMode$, { initialValue: 'over' });
  subscription = new Subscription();
  isMobileMenuOpened = signal(false);

  readonly sidenavWidthExpandedPx = 220;
  readonly sidenavWidthCollapsedPx = 68;
  readonly menuPadInlineStartPx = 40;
  readonly menuPadInlineEndDesktopPx = 83; // transparent overhang; doubles as the menu→content gutter
  readonly menuPadBlockPx = 56;

  readonly sidenavWidthExpanded = this.sidenavWidthExpandedPx + 'px';
  readonly sidenavWidthCollapsed = this.sidenavWidthCollapsedPx + 'px';
  readonly menuPadInlineStart = this.menuPadInlineStartPx + 'px';
  readonly menuPadBlock = this.menuPadBlockPx + 'px';

  // On Medium screens (960–1279px, where the drawer already auto-collapses) shrink the
  // trailing gutter to match the leading one instead of the wider desktop gap.
  menuPadInlineEndPx = computed(() =>
    this.responsiveService.medium() ? this.menuPadInlineStartPx : this.menuPadInlineEndDesktopPx
  );
  menuPadInlineEnd = computed(() => this.menuPadInlineEndPx() + 'px');

  // The drawer is wider than the visible menu panel: its inline padding is a transparent
  // overhang whose trailing half is the gutter before the page content. So the drawer's
  // outer width is exactly where the content pane should start.
  sidenavWidth = computed(() => {
    if (!this.showMenu() || this.iframeDetection.inIframe()) {
      return '0px';
    }
    const panel = this.layoutService.drawerShrink()
      ? this.sidenavWidthCollapsedPx
      : this.sidenavWidthExpandedPx;
    return panel + this.menuPadInlineStartPx + this.menuPadInlineEndPx() + 'px';
  });

  @ViewChild('snav') snav!: MatDrawer;
  @ViewChild('sidenavContent') sidenavContent!: ElementRef<HTMLElement>;

  @HostListener('window:scroll', [])
  divScroll() {
    this.isScrolled.set(true);
  }

  ngOnInit(): void {
    this.checkForUpdate.checkForUpdate();
    this.promptUpdate.checkUpdates();
    this.handleUnrecoverableState.checkUnrecoverableState();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const activeRoute = this.getActiveRoute(this.activatedRoute);
      activeRoute.data.subscribe((value: Data) => {
        if (value['hideNavbar'] || this.iframeDetection.inIframe()) {
          this.hideNavbar.set(true);
        } else {
          this.sectionData.set(value as ISectionData);
          this.hideNavbar.set(false);
        }
      });
    });

    window.addEventListener('message', (event) => {
      // implement auth from iframe
      if (event.data?.action === 'auth') {
        const token = event.data.token;
        console.log('Token:', token);
      }
    });
    this.matIconRegistry.setDefaultFontSetClass('material-symbols-rounded');
    this.matIconRegistry.registerFontClassAlias('hgi', 'hgi hgi-stroke');
  }

  ngAfterViewInit(): void {
    this.subscription.add(
      this.responsiveService.medium$.subscribe((medium) =>
        this.layoutService.setDrawerShrink(medium)
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getActiveRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  closeMenu() {
    this.closeMenuSignal.set(true);
  }

  async startCloseSidenav() {
    if (await firstValueFrom(this.responsiveService.medium$)) {
      void this.snav.open();
      this.layoutService.setDrawerShrink(true);
    }
  }
}
